/**
 * Everything the monitoring template page reads that does not change per
 * period: the template (its recipe), its borrowers and its periods.
 *
 * One hook for three requests because they are one screen: the header needs
 * the template AND the borrower count, and the period chips have to exist
 * before a board can be asked for. Loading them separately would give the page
 * three independent spinners for a single answer.
 *
 * Writes (`addBorrower`, `addPeriod`) refresh only the list they changed —
 * creating a period does not move a borrower — except that a new borrower
 * changes `borrowers_count` in the header, so it refetches the template too.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { monitoringService } from "../services/monitoringService";
import type {
	MonitoringBorrower,
	MonitoringBorrowerCandidateGroup,
	MonitoringCreateBorrowerPayload,
	MonitoringCreatePeriodPayload,
	MonitoringPeriod,
	MonitoringTemplate,
	MonitoringUpdateTemplatePayload,
} from "../types/monitoring";

export interface MonitoringTemplateData {
	template: MonitoringTemplate | null;
	borrowers: MonitoringBorrower[];
	/**
	 * The names the user's own documents carry, for the strict picker (DOP-454).
	 * Empty is a real answer — no borrower documents have been uploaded — and so
	 * is empty after a failure: the candidate list is an aid to adding a
	 * borrower, never a reason the page cannot render.
	 */
	borrowerCandidates: MonitoringBorrowerCandidateGroup[];
	periods: MonitoringPeriod[];
	loading: boolean;
	error: Error | undefined;
	refresh: () => void;
	/** Resolves to the created borrower, or rejects with the service's error. */
	addBorrower: (
		payload: MonitoringCreateBorrowerPayload,
	) => Promise<MonitoringBorrower>;
	addPeriod: (payload: MonitoringCreatePeriodPayload) => Promise<MonitoringPeriod>;
	/** Edit the recipe (DOP-456); resolves to the stored template. */
	updateTemplate: (
		patch: MonitoringUpdateTemplatePayload,
	) => Promise<MonitoringTemplate>;
}

export function useMonitoringTemplate(
	templateId: string | undefined,
): MonitoringTemplateData {
	const [template, setTemplate] = useState<MonitoringTemplate | null>(null);
	const [borrowers, setBorrowers] = useState<MonitoringBorrower[]>([]);
	const [borrowerCandidates, setBorrowerCandidates] = useState<
		MonitoringBorrowerCandidateGroup[]
	>([]);
	const [periods, setPeriods] = useState<MonitoringPeriod[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// The template this hook is currently answering about. Read by in-flight
	// requests when they land, so a response knows whether it is still the
	// answer to the question on screen.
	const activeTemplateId = useRef(templateId);
	activeTemplateId.current = templateId;

	// Rising counter, so a response for the template the user just navigated
	// away from cannot overwrite the one now on screen.
	const requestId = useRef(0);
	// The candidates need their OWN counter, not a share of `requestId`: they
	// are refetched after every write without reloading the recipe, so one
	// counter would let an add's candidate refresh invalidate an in-flight
	// recipe load and vice versa.
	const candidatesRequestId = useRef(0);

	/**
	 * Fetched on its own rather than inside the page's `Promise.all`: a failure
	 * here must cost the picker its suggestions, not the whole template its
	 * render. It is also the one request that goes stale on every write, so it
	 * has to be callable without refetching the recipe.
	 *
	 * Guarded by generation AND by template id. `mounted` is not enough: the
	 * page component stays mounted when the route moves from template A to
	 * template B, so A's response landing after the switch used to write A's
	 * names into B's picker — and those names are submittable, which is exactly
	 * the wrong-borrower-for-this-fund outcome the strict picker exists to
	 * prevent.
	 */
	const loadCandidates = useCallback(async () => {
		if (!templateId) return;
		const id = ++candidatesRequestId.current;
		const forTemplate = templateId;
		const current = () =>
			mounted.current &&
			id === candidatesRequestId.current &&
			forTemplate === activeTemplateId.current;
		try {
			const next = await monitoringService.listBorrowerCandidates(forTemplate);
			if (current()) setBorrowerCandidates(next);
		} catch {
			// Best effort, still: no suggestions is a usable page. But a FAILED
			// request for A must not clear B's list either.
			if (current()) setBorrowerCandidates([]);
		}
	}, [templateId]);

	const load = useCallback(async () => {
		if (!templateId) {
			setLoading(false);
			setError(new Error("No template was named in the URL."));
			return;
		}
		const id = ++requestId.current;
		setLoading(true);
		setError(undefined);
		try {
			const [nextTemplate, nextBorrowers, nextPeriods] = await Promise.all([
				monitoringService.getTemplate(templateId),
				monitoringService.listBorrowers(templateId),
				monitoringService.listPeriods(templateId),
			]);
			if (!mounted.current || id !== requestId.current) return;
			setTemplate(nextTemplate);
			setBorrowers(nextBorrowers);
			setPeriods(nextPeriods);
		} catch (caught) {
			if (!mounted.current || id !== requestId.current) return;
			// A failed reload must not leave the previous template's recipe under
			// this template's heading — that is a wrong answer, not a stale one.
			setTemplate(null);
			setError(caught instanceof Error ? caught : new Error(String(caught)));
		} finally {
			if (mounted.current && id === requestId.current) setLoading(false);
		}
	}, [templateId]);

	useEffect(() => {
		void load();
	}, [load]);

	/**
	 * Edit the template (DOP-456). The server's response REPLACES the template in
	 * state — not the optimistic value the caller sent — so the Metrics tab always
	 * shows what is stored. A rejection is left to the caller to report: the page
	 * has the toast, and swallowing it here would leave the radio showing a
	 * setting that never saved.
	 */
	const updateTemplate = useCallback(
		async (patch: MonitoringUpdateTemplatePayload) => {
			if (!templateId) throw new Error("No template was named in the URL.");
			const saved = await monitoringService.updateTemplate(templateId, patch);
			if (mounted.current) setTemplate(saved);
			return saved;
		},
		[templateId],
	);
	useEffect(() => {
		// Cleared on the way in, not only replaced on the way out: between the
		// switch to B and B's response there is a render, and showing A's names
		// under B's heading for even that long offers a borrower this fund does
		// not lend to.
		setBorrowerCandidates([]);
		void loadCandidates();
	}, [loadCandidates]);

	const addBorrower = useCallback(
		async (payload: MonitoringCreateBorrowerPayload) => {
			if (!templateId) throw new Error("No template was named in the URL.");
			// The POST is the write, and its response IS the result. Everything
			// after it is housekeeping: the borrower list, the header's count and
			// the candidate list are all re-reads of a row that already exists.
			//
			// So a read that fails afterwards must NOT be reported as a failed
			// add. It used to be — this function awaited the reads and let them
			// reject — and the page then showed "Could not add the borrower" over
			// a form still holding the name, so the obvious retry created a
			// SECOND one. A stale count is a smaller lie than a duplicate row.
			const created = await monitoringService.createBorrower(templateId, payload);
			try {
				const [nextBorrowers, nextTemplate] = await Promise.all([
					monitoringService.listBorrowers(templateId),
					// The header counts borrowers; a new one has to change that count.
					monitoringService.getTemplate(templateId),
				]);
				if (mounted.current) {
					setBorrowers(nextBorrowers);
					setTemplate(nextTemplate);
				}
				// The server drops whoever is on the roster from the candidate list,
				// so every add makes it stale — and "add all from this folder" adds
				// several in a row, each of which must disappear from the next offer.
				await loadCandidates();
			} catch {
				// Fall back to what we know: the borrower the server just returned.
				// The template's count and the candidate list stay as they were —
				// guessing at them would put numbers on screen no server said —
				// and the next `refresh()` reconciles all three.
				if (mounted.current) {
					setBorrowers((current) =>
						current.some((borrower) => borrower.public_id === created.public_id)
							? current
							: [...current, created],
					);
				}
			}
			return created;
		},
		[templateId, loadCandidates],
	);

	const addPeriod = useCallback(
		async (payload: MonitoringCreatePeriodPayload) => {
			if (!templateId) throw new Error("No template was named in the URL.");
			const created = await monitoringService.createPeriod(templateId, payload);
			// The POST is the answer: once it returns, the period exists. A failed
			// refresh after it must not turn that into a failure — the form would
			// keep what was typed and invite a retry that only meets a conflict.
			try {
				const nextPeriods = await monitoringService.listPeriods(templateId);
				if (mounted.current) setPeriods(nextPeriods);
			} catch {
				// Fall back to what we know: the period the server just returned,
				// in period-end order like the list. The next `refresh()` reconciles.
				if (mounted.current) {
					setPeriods((current) =>
						current.some((period) => period.public_id === created.public_id)
							? current
							: [...current, created].sort((a, b) => a.period_end.localeCompare(b.period_end)),
					);
				}
			}
			return created;
		},
		[templateId],
	);

	return useMemo(
		() => ({
			template,
			borrowers,
			borrowerCandidates,
			periods,
			loading,
			error,
			refresh: () => {
				void load();
				void loadCandidates();
			},
			addBorrower,
			addPeriod,
			updateTemplate,
		}),
		[
			template,
			borrowers,
			borrowerCandidates,
			periods,
			loading,
			error,
			load,
			loadCandidates,
			addBorrower,
			addPeriod,
			updateTemplate,
		],
	);
}
